import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { showModalPopup } from "../../../App/state/actions";
import moment from "moment";
import constants from "../../../shared/constants";
import PlayerConstants from "../../../shared/constants/player";
import errorConstants from "../../../shared/constants/error";
import { isPlatformAllowToPlay } from "../../../shared/utils";
import { createVideoStream, getStreamType } from "../../../shared/utils/playbackHelpers";
const { ERROR_TYPES } = errorConstants;
const { STREAM_MODE, PLAYER_STREAM_CONFIG, STREAM_FORMAT_TYPES, DRM_SYSTEM_TYPES } = PlayerConstants;
const { MODAL_TYPES } = constants;

const usePlayerStreamInfo = ({
  isLivePlayer,
  videoUrl,
  authToken,
  appProvider,
  currentEpisodeId,
  isRecordingPlayer,
  externalId,
  initPlaybackTimestamp,
  passFeatureFlags,
  setStreamInfo,
  prevAuthToken,
  assetId,
  bookmark,
  history,
  redirectUri,
  miniGuideOnNowPrograms,
  currentChannelId,
  prevVideoUrl,
  setHasChannelTuneInfo,
  restartLiveStream,
  isRestartedStream,
  currentChannel,
  isInHome,
  isLookbackStream,
  streamInfo,
}) => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  useEffect(() => {
    // Static stream info.
    // Live player streamInfo logic separated for channel tune performance feature
    if (isLivePlayer) return; // TODO: merge streamInfo logic once the tune performance backend support is added to VOD/Recording playback, see TCDWC-1474
    if (!videoUrl || !authToken || prevAuthToken.current === authToken) return;
    // Check for unsupported platforms
    if (!isPlatformAllowToPlay(appProvider)) {
      const modalContent = {
        title: ERROR_TYPES.INFO,
        history,
        isCloseable: true,
        message: translate("error_playback_browser"),
      };

      dispatch(showModalPopup(MODAL_TYPES.ERROR, modalContent));
      return;
    }
    const uniContentId = currentEpisodeId;
    const streamMode = STREAM_MODE.VOD;
    const isHLS = getStreamType(videoUrl) === STREAM_FORMAT_TYPES.HLS;
    const playbackType = isHLS ? STREAM_FORMAT_TYPES.HLS : STREAM_FORMAT_TYPES.DASH;
    const drmType = isHLS ? DRM_SYSTEM_TYPES.FP : DRM_SYSTEM_TYPES.WV;
    let licenseURL, licenseCertUrl;
    if (isHLS) {
      licenseURL = PLAYER_STREAM_CONFIG.vod.licenseURL.hls;
      licenseCertUrl = redirectUri + PLAYER_STREAM_CONFIG.vod.licenseCertUrl.hls;
    } else {
      licenseURL = PLAYER_STREAM_CONFIG.vod.licenseURL.dash;
      licenseCertUrl = null;
    }
    const externalContentId = isRecordingPlayer ? externalId : "";

    initPlaybackTimestamp.current = moment().valueOf(); // Set the initial playback timestamp for the current VOD asset

    // Check if partial panic is turn on
    if (!passFeatureFlags()) return;

    setStreamInfo(
      createVideoStream(
        streamMode,
        videoUrl,
        playbackType,
        drmType,
        licenseURL,
        licenseCertUrl,
        authToken,
        assetId,
        uniContentId,
        bookmark,
        externalContentId
      )
    );
    prevAuthToken.current = authToken;
  }, [
    assetId,
    authToken,
    bookmark,
    currentEpisodeId,
    externalId,
    isLivePlayer,
    isRecordingPlayer,
    videoUrl,
    appProvider,
    history,
    dispatch,
    translate,
    passFeatureFlags,
    redirectUri,
    initPlaybackTimestamp,
    prevAuthToken,
    setStreamInfo,
  ]);

  useEffect(() => {
    // Live player streamInfo logic separated for channel tune performance feature
    if (miniGuideOnNowPrograms) {
      // Check for unsupported platforms
      if (!isPlatformAllowToPlay(appProvider)) {
        const modalContent = {
          title: ERROR_TYPES.INFO,
          history,
          isCloseable: true,
          message: translate("error_playback_browser"),
        };

        dispatch(showModalPopup(MODAL_TYPES.ERROR, modalContent));
        return;
      }
      if (!isLivePlayer) return;
      if (!videoUrl) return; // Don't create streamInfo with empty/null manifest URL
      if (prevVideoUrl.current === videoUrl) {
        if (appProvider.ENV === "avs-prod") return;
        else if (currentChannelId === streamInfo?.contentId) return;
      }
      const uniContentId = currentChannelId;
      const streamMode = STREAM_MODE.LIVE;
      const isHLS = getStreamType(videoUrl) === STREAM_FORMAT_TYPES.HLS;
      const playbackType = isHLS ? STREAM_FORMAT_TYPES.HLS : STREAM_FORMAT_TYPES.DASH;
      const drmType = isHLS ? DRM_SYSTEM_TYPES.FP : DRM_SYSTEM_TYPES.WV;
      let licenseURL, licenseCertUrl;
      if (isHLS) {
        licenseURL = PLAYER_STREAM_CONFIG.live.licenseURL.hls;
        licenseCertUrl = redirectUri + PLAYER_STREAM_CONFIG.live.licenseCertUrl.hls;
      } else {
        licenseURL = PLAYER_STREAM_CONFIG.live.licenseURL.dash;
        licenseCertUrl = null;
      }

      initPlaybackTimestamp.current = moment().valueOf(); // Set the initial playback timestamp for the current Live asset

      // Check if partial panic is turn on
      if (!passFeatureFlags()) return;

      setStreamInfo(
        createVideoStream(
          streamMode,
          videoUrl,
          playbackType,
          drmType,
          licenseURL,
          licenseCertUrl,
          null,
          assetId,
          uniContentId
        )
      );
      setHasChannelTuneInfo(false);
      prevVideoUrl.current = videoUrl;
      if (restartLiveStream) {
        isRestartedStream.current = true;
      }
    }
  }, [
    assetId,
    currentChannelId,
    videoUrl,
    restartLiveStream,
    appProvider,
    currentChannel,
    isInHome,
    isLookbackStream,
    miniGuideOnNowPrograms,
    isLivePlayer,
    history,
    dispatch,
    translate,
    passFeatureFlags,
    redirectUri,
    initPlaybackTimestamp,
    prevVideoUrl,
    setStreamInfo,
    setHasChannelTuneInfo,
    isRestartedStream,
    streamInfo,
  ]);
};

export default usePlayerStreamInfo;
